import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/**
 * Valida se um CNPJ é válido de acordo com o algoritmo da Receita Federal.
 * Suporta o novo formato alfanumérico (letras A-Z nas 12 primeiras posições)
 * implantado para novas inscrições.
 *
 * Regra de conversão para letras A-Z:
 * Subtrai-se o valor 48 do código ASCII do caractere.
 * Exemplo: 'A' = 65 - 48 = 17, 'Z' = 90 - 48 = 42.
 *
 * @param value CNPJ a ser validado (com ou sem formatação)
 */
export function cnpjIsValid(value: string | null | undefined): boolean {
  if (!value || typeof value !== 'string') {
    return false;
  }

  // Remove caracteres de formatação (mantém apenas letras e números)
  const cleaned = value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

  // O CNPJ deve possuir exatamente 14 caracteres após a limpeza
  if (cleaned.length !== 14) {
    return false;
  }

  // As primeiras 12 posições podem conter letras (A-Z) e números (0-9).
  // Os dois últimos dígitos (DV) devem ser obrigatoriamente numéricos.
  if (!/^[A-Z0-9]{12}[0-9]{2}$/.test(cleaned)) {
    return false;
  }

  // Rejeita CNPJs com todos os caracteres idênticos (ex: "00000000000000", "AAAAAAAAAAAAAA")
  if (/^(.)\1+$/.test(cleaned)) {
    return false;
  }

  // Cálculo do primeiro dígito verificador
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum1 = 0;
  for (let i = 0; i < 12; i++) {
    const val = cleaned.charCodeAt(i) - 48;
    sum1 += val * weights1[i];
  }
  const remainder1 = sum1 % 11;
  const digit1 = remainder1 < 2 ? 0 : 11 - remainder1;

  // Cálculo do segundo dígito verificador
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum2 = 0;
  for (let i = 0; i < 13; i++) {
    const val = cleaned.charCodeAt(i) - 48;
    sum2 += val * weights2[i];
  }
  const remainder2 = sum2 % 11;
  const digit2 = remainder2 < 2 ? 0 : 11 - remainder2;

  // Validação dos dígitos verificadores calculados com os informados
  return digit1 === Number(cleaned[12]) && digit2 === Number(cleaned[13]);
}

/**
 * Validador Angular (ValidatorFn) para formulários reativos (Reactive Forms).
 * Exemplo de uso:
 * `cnpj: [null, cnpjValidator]`
 */
export const cnpjValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;

  // Se o valor estiver vazio, não valida (deixa para o Validators.required se necessário)
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const isValid = cnpjIsValid(value);
  return isValid ? null : { cnpj: true };
};
